import { useState } from "react";
import { api, apiError } from "../lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import { Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

// Discreet delete control with a double confirmation (two chained dialogs).
export function DeleteStudyButton({ b, onDeleted }) {
  const [step, setStep] = useState(0); // 0 closed · 1 first confirm · 2 final confirm
  const [loading, setLoading] = useState(false);

  const remove = async () => {
    setLoading(true);
    try {
      await api.delete(`/bookings/${b.id}`);
      toast.success("Estudio eliminado");
      setStep(0);
      onDeleted();
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setStep(1)}
        title="Eliminar estudio"
        aria-label="Eliminar estudio"
        className="text-slate-300 hover:text-red-500 transition-colors p-1"
        data-testid={`delete-study-${b.id}`}
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>

      {/* First confirmation */}
      <AlertDialog open={step === 1} onOpenChange={(o) => { if (!o) setStep(0); }}>
        <AlertDialogContent data-testid={`delete-study-confirm1-${b.id}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este estudio?</AlertDialogTitle>
            <AlertDialogDescription>
              {b.case_number ? <><strong>{b.case_number}</strong> · </> : null}{b.patient_name} · {b.clinic_name}.
              Se borrarán los datos, el informe PDF y la conversación. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`delete-study-cancel1-${b.id}`}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); setStep(2); }}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid={`delete-study-next-${b.id}`}
            >
              Continuar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Final confirmation */}
      <AlertDialog open={step === 2} onOpenChange={(o) => { if (!o && !loading) setStep(0); }}>
        <AlertDialogContent data-testid={`delete-study-confirm2-${b.id}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmación final</AlertDialogTitle>
            <AlertDialogDescription>
              Vas a eliminar <strong>definitivamente</strong> el estudio{b.case_number ? <> <strong>{b.case_number}</strong></> : null}.
              No habrá forma de recuperarlo. ¿Seguro que quieres continuar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={loading} data-testid={`delete-study-cancel2-${b.id}`}>No, conservar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); remove(); }}
              disabled={loading}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid={`delete-study-final-${b.id}`}
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Eliminar definitivamente"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
